import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from '../../../../i18n';
import { getFiGrowthBook } from '../../../lib/growthbook';
import styles from './UpgradeSplash.module.scss';
import * as types from '../../../types';
import { getPriceForRateUnit, centsToDollarsNumber } from '../../../lib/util';
import classNames from 'classnames';
import { useLocale } from '../../../contexts/LocaleContext';

interface UpgradeOfferProps {
  offeredPlan: types.ISubscriptionProduct;
}

interface SelectableUpgradeOfferProps extends UpgradeOfferProps {
  onSelect: (subscriptionProduct: types.ISubscriptionProduct) => void;
  promotionalDiscountAmountInCents: number;
  selected: boolean;
}

function renewalTitle(subscriptionProduct: types.ISubscriptionProduct) {
  if (subscriptionProduct.buyItMembership) {
    return i18next.t('buy_it');
  }

  const renewalMonths = subscriptionProduct.renewalMonths;
  if (renewalMonths % 12 === 0) {
    const years = Math.round(renewalMonths / 12);
    return i18next.t('subscription_years_year_uppercase', { years });
  }

  if (renewalMonths === 1) {
    return i18next.t('subscription_monthly_uppercase');
  }

  return i18next.t('subscription_months_uppercase', { months: renewalMonths });
}

export function UpgradeOffer({ offeredPlan }: UpgradeOfferProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.offer}>
        <div className={styles.offerLabel}>{t('your_offer')}</div>
        <div className={styles.offerHeader}>
          <h1>
            {offeredPlan.displayedRate}
            {'/'}
            {offeredPlan.displayedRateUnit}
          </h1>
        </div>
        <div className={styles.offerBody}>
          <div>
            {t('savings_and_get_credit_for_remainder_of_series_2_subscription', { savings: offeredPlan.savings })}
          </div>
        </div>
      </div>
    </>
  );
}

export function SelectableUpgradeOffer({
  offeredPlan,
  onSelect,
  promotionalDiscountAmountInCents,
  selected,
}: SelectableUpgradeOfferProps) {
  const { currency, currencyFormatter } = useLocale();
  const { t } = useTranslation();
  const { unit: displayRateUnit, displayRate } = getPriceForRateUnit(offeredPlan, 'week', currency, currencyFormatter);
  const onClick = useCallback(() => onSelect(offeredPlan), [offeredPlan, onSelect]);
  const blackFridayCyberMondayEnabled =
    getFiGrowthBook().getFeatureValue<string>('black-friday-cyber-monday-upgrade-flow-coupon-code', '') !== '';

  const originalActivationFeeInCents = offeredPlan.discountedFromOriginalActivationFeeInCents ?? 0;
  const activationFeeInCents = offeredPlan.activationFeeInCents ?? 0;
  const activationFeeDiscountInCents = originalActivationFeeInCents - activationFeeInCents;

  const totalDiscountInCents = promotionalDiscountAmountInCents + activationFeeDiscountInCents;

  return (
    <>
      <div
        className={classNames(styles.offer, styles.selectable, {
          [styles.selected]: !!selected,
        })}
        onClick={onClick}
      >
        <div className={styles.offerLabel}>{renewalTitle(offeredPlan)}</div>
        <div className={styles.offerHeader}>
          <h2>
            {displayRate}
            {'/'}
            {displayRateUnit}
          </h2>
          {blackFridayCyberMondayEnabled &&
            offeredPlan.couponExtensionMonths &&
            offeredPlan.couponExtensionMonths > 0 && (
              <div className={styles.offerDiscount}>
                <div className={styles.offerDiscountAmount}>
                  {t('plus_months_free_uppercase', { months: offeredPlan.couponExtensionMonths })}
                </div>
              </div>
            )}
          {!blackFridayCyberMondayEnabled && totalDiscountInCents > 0 && (
            <div className={styles.offerDiscount}>
              <div className={styles.offerDiscountAmount}>
                {t('plus_dollar_amount_off', { amount: centsToDollarsNumber(totalDiscountInCents) })}
              </div>
            </div>
          )}
        </div>
        <div className={styles.offerFooter}>
          {promotionalDiscountAmountInCents > 0 && (
            <div>
              {t('dollar_amount_offer_credit', { amount: centsToDollarsNumber(promotionalDiscountAmountInCents) })}
            </div>
          )}
          {activationFeeDiscountInCents > 0 && (
            <div>
              {t('activation_fee_amount_waived', { amount: centsToDollarsNumber(originalActivationFeeInCents) })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
